import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    Users,
    UserCheck,
    Loader2,
    Briefcase,
    ClipboardList,
    Wrench,
} from "lucide-react";
import { toast } from "react-toastify";
import PageBreadcrumb from "../../../../shared/PageBreadCrumb";
import amsProjectService from "../../../../services/amsProjectService";

export default function AmsProjectAssignments() {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const [project, setProject] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!projectId) return;
        amsProjectService
            .getProjectById(projectId)
            .then((data) => setProject(data))
            .catch((e: any) => {
                toast.error(e?.message ?? "Failed to load project");
                navigate("/projects");
            })
            .finally(() => setLoading(false));
    }, [projectId]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12 text-center">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">Project not found</p>
            </div>
        );
    }

    const auditors: any[] = project.assignedResources?.auditors ?? [];
    const projectManager = project.assignedResources?.projectManager ?? project.manager;
    const projectHead = project.assignedResources?.projectHead;

    return (
        <div className="max-w-5xl mx-auto px-3 sm:px-4 pb-6">
            <PageBreadcrumb
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Projects", path: "/projects" },
                    {
                        name: project.projectName ?? project.name ?? "Project",
                        path: `/projects/view/${projectId}`,
                    },
                    { name: "Assignments", path: undefined },
                ]}
            />

            <div className="flex items-center gap-2 mb-4">
                <button
                    onClick={() => navigate(`/projects/view/${projectId}`)}
                    className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back
                </button>
                <div className="h-4 w-px bg-slate-200" />
                <h1 className="text-lg font-bold text-slate-900">
                    Assignments — {project.projectName ?? project.name}
                </h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Current team */}
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                >
                    <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-teal-600" />
                        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                            Project Team
                        </h3>
                    </div>
                    <div className="p-3 space-y-3">
                        {projectManager && (
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-[11px] flex-shrink-0">
                                    {(projectManager.name ?? "M").charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-xs font-medium text-slate-800 truncate">
                                        {projectManager.name ?? projectManager}
                                    </div>
                                    <div className="text-[10px] text-slate-500">Project Manager</div>
                                </div>
                            </div>
                        )}
                        {projectHead && (
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-[11px] flex-shrink-0">
                                    {(projectHead.name ?? "H").charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-xs font-medium text-slate-800 truncate">
                                        {projectHead.name ?? projectHead}
                                    </div>
                                    <div className="text-[10px] text-slate-500">Project Head</div>
                                </div>
                            </div>
                        )}
                        {auditors.length === 0 && !projectManager && !projectHead && (
                            <p className="text-xs text-slate-400 italic py-2">No team members assigned yet</p>
                        )}
                    </div>
                </motion.div>

                {/* Auditors */}
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05, duration: 0.2 }}
                    className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                >
                    <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center gap-1.5">
                        <UserCheck className="w-3.5 h-3.5 text-teal-600" />
                        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                            Auditors ({auditors.length})
                        </h3>
                    </div>
                    <div className="p-3">
                        {auditors.length > 0 ? (
                            <div className="space-y-2">
                                {auditors.map((auditor: any, i: number) => (
                                    <div key={auditor._id ?? i} className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-[11px] flex-shrink-0">
                                            {(auditor.name ?? "A").charAt(0).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-xs font-medium text-slate-800 truncate">
                                                {auditor.name}
                                            </div>
                                            {auditor.email && (
                                                <div className="text-[10px] text-slate-500 truncate">
                                                    {auditor.email}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-slate-400 italic py-2">No auditors assigned</p>
                        )}
                    </div>
                </motion.div>
            </div>

            {/* Coming-soon panel for task-level dispatch */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.2 }}
                className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center"
            >
                <div className="flex justify-center gap-3 mb-4 opacity-40">
                    <ClipboardList className="w-8 h-8 text-teal-600" />
                    <Wrench className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700 mb-1">
                    Task-level Auditor Dispatch — Coming Soon
                </h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    Assign specific auditors to individual audit checkpoints and equipment
                    inspections. This feature is under development for the AMS module.
                </p>
                <button
                    onClick={() => navigate(`/projects/edit/${projectId}`)}
                    className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md transition-colors"
                >
                    <Users className="w-3.5 h-3.5" />
                    Manage team from project form
                </button>
            </motion.div>
        </div>
    );
}
